/**
 * Shared presentational primitives for the AI Center pages.
 *
 * Visual language follows the agent workspace (components/agent/*): white
 * cards on a light canvas, `#3454cf` accent, soft 8–12px corners, hairline
 * gray-200 borders, and shadow only on elevation — deliberately not the
 * PrimeReact card/button chrome used elsewhere in Admin.
 *
 * These live outside the page files so AI Configuration and AI Playground
 * cannot drift apart visually.
 */
import type { JSX, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

/**
 * PrimeReact input overrides — the theme's own corners are squarer and its
 * borders heavier than the rest of these pages.
 */
export const INPUT_CLASS =
  'w-full !rounded-sm !border-gray-200 !bg-white !px-3 !py-2 !text-sm !shadow-none focus:!border-[#3454cf] focus:!ring-2 focus:!ring-[#3454cf]/20'

/**
 * Dropdown's root is a wrapper div, not the field itself — padding belongs on
 * the inner label, which the theme already sizes. Only the frame is restyled.
 */
export const DROPDOWN_CLASS =
  'w-full !rounded-sm !border-gray-200 !bg-white !shadow-none [&_.p-dropdown-trigger]:!rounded-r-sm [&_.p-dropdown-label]:!rounded-l-sm [&_.p-dropdown-label]:!px-3 [&_.p-dropdown-label]:!py-2 [&_.p-dropdown-label]:!text-sm [&_.p-dropdown-label]:!leading-5 focus-within:!border-[#3454cf] focus-within:!ring-2 focus-within:!ring-[#3454cf]/20'

export const DROPDOWN_PANEL_CLASS =
  '!rounded-sm !border-gray-200 !text-sm [&_.p-dropdown-item]:!rounded-sm [&_.p-dropdown-filter]:!rounded-sm'

export function Section({
  icon,
  title,
  description,
  action,
  children,
  className,
}: {
  icon: ReactNode
  title: ReactNode
  description?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <section className={`mb-5 overflow-hidden rounded-sm border border-gray-200 bg-white shadow-sm${className ? ` ${className}` : ''}`}>
      <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-[#3454cf]/10 text-[#3454cf]">
            {icon}
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {description !== undefined && (
              <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{description}</p>
            )}
          </div>
        </div>
        {action !== undefined && <div className="shrink-0">{action}</div>}
      </header>
      <div className="min-w-0 px-5 py-5">{children}</div>
    </section>
  )
}

/** Badge that tells an inherited platform default from a tenant override. */
export function OverrideTag({ overridden }: { overridden: boolean }): JSX.Element {
  return (
    <span
      className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium leading-4 ${
        overridden ? 'bg-[#3454cf]/10 text-[#3454cf]' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {overridden ? 'Custom' : 'Inherited'}
    </span>
  )
}

/** Read-only value cell — monospaced so model names stay scannable. */
export function ValueBox({ value }: { value: string | undefined }): JSX.Element {
  return (
    <div className="min-w-0 break-all rounded-sm border border-gray-200 bg-gray-50/80 px-3 py-2 font-mono text-xs text-gray-800">
      {value && value.length > 0 ? value : <span className="text-gray-400">—</span>}
    </div>
  )
}

export function Field({
  label,
  badge,
  hint,
  children,
}: {
  label: string
  badge?: ReactNode
  hint?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex min-w-0 items-center gap-2">
        <label className="text-[13px] font-medium text-gray-700">{label}</label>
        {badge}
        {hint}
      </div>
      {children}
    </div>
  )
}

/** Neutral metadata pill — run latency, token counts, resolved model. */
export function Chip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="rounded-sm bg-white/70 px-2 py-0.5 font-mono text-[11px] text-gray-600 ring-1 ring-black/5">
      {children}
    </span>
  )
}

export function SectionCard({
  title,
  children,
  className = '',
}: {
  title?: string
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div className={`rounded-sm border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
      {title && <h3 className="mb-3 text-sm font-semibold text-gray-900">{title}</h3>}
      {children}
    </div>
  )
}

type ButtonTone = 'primary' | 'secondary' | 'ghost'

export function ActionButton({
  tone = 'secondary',
  icon,
  label,
  busy = false,
  disabled = false,
  onClick,
}: {
  tone?: ButtonTone
  icon?: ReactNode
  label: string
  busy?: boolean
  disabled?: boolean
  onClick: () => void
}): JSX.Element {
  const tones: Record<ButtonTone, string> = {
    primary: 'bg-[#3454cf] text-white shadow-sm hover:bg-[#2a45b0] active:scale-[0.98]',
    secondary:
      'bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50 hover:border-gray-300 active:scale-[0.98]',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:scale-[0.98]',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded-sm px-3.5 text-[13px] font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3454cf]/40 disabled:pointer-events-none disabled:opacity-50 ${tones[tone]}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
    </button>
  )
}
